// ============================================================================
//  Organigramme — Phase 5-B
//  Remplace : apps/frontend/src/pages/orgchart/OrgchartMain.tsx
//
//  Ajouts Phase 5-B :
//    1. Export PNG / SVG / PDF (html-to-image, client-side)
//    2. Dialog confirmation drag & drop avec aperçu d'impact RH
// ============================================================================

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Users,
  Download,
  X,
  Plus,
  User,
  Mail,
  Building2,
  ImageDown,
  FileDown,
  AlertTriangle,
  MoveRight,
  Check,
  Edit2,
  UserPlus,
  UserMinus,
} from 'lucide-react'
import { toPng, toSvg } from 'html-to-image'
import { orgchartApi, type OrgPosition } from '@/modules/orgchart/api/orgchart.api'
import api from '@/lib/axios'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const Spinner = () => (
  <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
)

// ─── Conversion positions → nodes/edges React Flow ───────────────────────────

function buildNodesEdges(positions: OrgPosition[]): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const byLevel: Record<number, OrgPosition[]> = {}
  positions.forEach((p) => {
    const lvl = p.level ?? 0
    if (!byLevel[lvl]) byLevel[lvl] = []
    byLevel[lvl].push(p)
  })

  const NODE_W = 220
  const NODE_H = 90
  const H_GAP = 48
  const V_GAP = 90

  Object.entries(byLevel).forEach(([levelStr, lvlPositions]) => {
    const level = Number(levelStr)
    const totalW = lvlPositions.length * (NODE_W + H_GAP) - H_GAP
    const startX = -totalW / 2

    lvlPositions.forEach((pos, i) => {
      nodes.push({
        id: pos.id,
        type: 'orgNode',
        position: {
          x: startX + i * (NODE_W + H_GAP),
          y: level * (NODE_H + V_GAP),
        },
        data: { position: pos },
      })

      if (pos.parentId) {
        edges.push({
          id: `e-${pos.parentId}-${pos.id}`,
          source: pos.parentId,
          target: pos.id,
          type: 'smoothstep',
          style: { stroke: '#334155', strokeWidth: 2 },
        })
      }
    })
  })

  return { nodes, edges }
}

// ─── Nœud personnalisé ────────────────────────────────────────────────────────

function OrgNode({ data, selected }: NodeProps) {
  const pos: OrgPosition = (data as any).position
  const user = pos.employee?.user

  return (
    <div
      className={`
      w-52 rounded-xl border transition-all cursor-pointer select-none
      ${
        pos.isVacant
          ? 'bg-white/60 border-dashed border-[var(--bd2)] opacity-70'
          : selected
            ? 'bg-[var(--surface-2)] border-blue-500 shadow-xl shadow-blue-500/20'
            : 'bg-white border-[var(--bd)] hover:border-blue-500/40 hover:shadow-lg'
      }
    `}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-[var(--surface-3)] !border-[var(--bd2)] !w-2 !h-2"
      />

      <div className="p-3">
        <div className="flex items-center gap-2.5 mb-2">
          {pos.isVacant ? (
            <div className="w-9 h-9 rounded-full border-2 border-dashed border-[var(--bd2)] flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-[var(--tx4)]" />
            </div>
          ) : user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="w-9 h-9 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center shrink-0">
              <span className="text-[var(--tx)] text-xs font-bold">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            {pos.isVacant ? (
              <p className="text-xs text-[var(--tx3)] italic">Poste vacant</p>
            ) : (
              <p className="text-xs font-semibold text-[var(--tx)] truncate">
                {user?.firstName} {user?.lastName}
              </p>
            )}
            <p className="text-[10px] text-[var(--tx3)] truncate">{pos.title}</p>
          </div>
        </div>

        {pos.department && (
          <div className="flex items-center gap-1">
            <Building2 className="w-3 h-3 text-[var(--tx3)] shrink-0" />
            <span className="text-[10px] text-[var(--tx3)] truncate">{pos.department.name}</span>
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-[var(--surface-3)] !border-[var(--bd2)] !w-2 !h-2"
      />
    </div>
  )
}

const NODE_TYPES = { orgNode: OrgNode }

// ============================================================================
//  PAGE PRINCIPALE
// ============================================================================

export default function OrgchartPage() {
  const queryClient = useQueryClient()
  const addNotif = useNotificationStore((s) => s.addNotification)
  const { hasPermission } = useAuthStore()
  const canManage = hasPermission('orgchart:manage')

  const [selectedPos, setSelectedPos] = useState<OrgPosition | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showVacant, setShowVacant] = useState(true)
  const [showExport, setShowExport] = useState(false)
  const [pendingMove, setPendingMove] = useState<{ nodeId: string; newParentId: string } | null>(
    null,
  )

  // Ref sur le wrapper React Flow pour l'export
  const flowWrapperRef = useRef<HTMLDivElement>(null)

  const { data: positions = [], isLoading } = useQuery({
    queryKey: ['orgchart-flat'],
    queryFn: () => orgchartApi.getFlat(),
  })

  const { data: stats } = useQuery({
    queryKey: ['orgchart-stats'],
    queryFn: () => orgchartApi.getStats(),
  })

  const filteredPositions = useMemo(() => {
    const all = positions as OrgPosition[]
    return showVacant ? all : all.filter((p) => !p.isVacant)
  }, [positions, showVacant])

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => buildNodesEdges(filteredPositions),
    [filteredPositions],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  useEffect(() => {
    const { nodes: n, edges: e } = buildNodesEdges(filteredPositions)
    setNodes(n)
    setEdges(e)
  }, [filteredPositions])

  // ── Mutation déplacement ──────────────────────────────────────────────────

  const moveMutation = useMutation({
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string }) =>
      orgchartApi.movePosition(id, newParentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
      queryClient.invalidateQueries({ queryKey: ['orgchart-stats'] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Position déplacée' })
      setPendingMove(null)
    },
    onError: () => {
      addNotif({ id: Date.now().toString(), type: 'error', title: 'Erreur lors du déplacement' })
      setPendingMove(null)
      // Remettre le graphe dans l'état serveur
      queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
    },
  })

  // ── Drag & drop : interception avant confirmation ─────────────────────────

  const onConnect = useCallback(
    (params: Connection) => {
      if (!canManage || !params.source || !params.target) return
      // Stocker le mouvement en attente et afficher le dialog de confirmation
      setPendingMove({ nodeId: params.target, newParentId: params.source })
    },
    [canManage],
  )

  const confirmMove = useCallback(() => {
    if (!pendingMove) return
    moveMutation.mutate({ id: pendingMove.nodeId, newParentId: pendingMove.newParentId })
    // Appliquer visuellement l'arête en attendant la réponse serveur
    setEdges((eds) =>
      addEdge(
        {
          source: pendingMove.newParentId,
          target: pendingMove.nodeId,
          type: 'smoothstep',
          style: { stroke: '#334155', strokeWidth: 2 },
        },
        eds.filter((e) => e.target !== pendingMove.nodeId),
      ),
    )
  }, [pendingMove, moveMutation])

  const cancelMove = useCallback(() => setPendingMove(null), [])

  const onNodeClick = useCallback((_: any, node: Node) => {
    const pos = (node.data as any).position as OrgPosition
    setSelectedPos(pos)
  }, [])

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExport = useCallback(
    async (format: 'png' | 'svg') => {
      const el = flowWrapperRef.current?.querySelector(
        '.react-flow__viewport',
      ) as HTMLElement | null
      if (!el) {
        addNotif({
          id: Date.now().toString(),
          type: 'error',
          title: 'Impossible de capturer le graphe',
        })
        return
      }

      try {
        addNotif({
          id: Date.now().toString(),
          type: 'info',
          title: `Export ${format.toUpperCase()} en cours…`,
        })

        let dataUrl: string
        const opts = {
          backgroundColor: 'var(--surface-2)', // slate-950
          pixelRatio: 2,
          style: { transform: 'scale(1)', transformOrigin: 'top left' },
        }

        if (format === 'png') {
          dataUrl = await toPng(el, opts)
        } else {
          dataUrl = await toSvg(el, opts)
        }

        const a = document.createElement('a')
        a.download = `organigramme-nexuscorp.${format}`
        a.href = dataUrl
        a.click()

        addNotif({
          id: Date.now().toString(),
          type: 'success',
          title: `Export ${format.toUpperCase()} téléchargé`,
        })
      } catch (err) {
        console.error(err)
        addNotif({ id: Date.now().toString(), type: 'error', title: "Erreur lors de l'export" })
      } finally {
        setShowExport(false)
      }
    },
    [addNotif],
  )

  if (isLoading)
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )

  // Données pour le dialog de confirmation — trouver les positions concernées
  const movedPosition = pendingMove
    ? filteredPositions.find((p) => p.id === pendingMove.nodeId)
    : null
  const newParentPosition = pendingMove
    ? filteredPositions.find((p) => p.id === pendingMove.newParentId)
    : null
  const oldParentPosition = movedPosition?.parentId
    ? filteredPositions.find((p) => p.id === movedPosition.parentId)
    : null

  // Compter les subordonnés du nœud déplacé (impact RH)
  const countSubordinates = (posId: string): number => {
    const direct = filteredPositions.filter((p) => p.parentId === posId)
    return direct.length + direct.reduce((s, p) => s + countSubordinates(p.id), 0)
  }
  const subordinateCount = pendingMove ? countSubordinates(pendingMove.nodeId) : 0

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] overflow-hidden rounded-xl border border-[var(--bd)]">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-[var(--bd)] shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-sm font-semibold text-[var(--tx)]">Organigramme NexusCorp</h1>
          {stats && (
            <span className="text-[10px] text-[var(--tx3)]">
              {(stats as any).occupied ?? 0} postes occupés
              {((stats as any).vacant ?? 0) > 0 && (
                <span className="text-[var(--amber-text)]"> · {(stats as any).vacant} vacants</span>
              )}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showVacant}
              onChange={(e) => setShowVacant(e.target.checked)}
              className="rounded border-[var(--bd2)] bg-[var(--surface-2)] text-blue-500"
            />
            <span className="text-xs text-[var(--tx3)]">Postes vacants</span>
          </label>

          {/* Bouton export avec dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExport((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[var(--bd)] rounded-lg text-xs text-[var(--tx2)] hover:text-[var(--tx)] hover:border-[var(--bd2)] transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export
            </button>
            {showExport && (
              <div
                className="absolute right-0 top-8 nx-card shadow-2xl z-30 min-w-40 py-1"
                onMouseLeave={() => setShowExport(false)}
              >
                <button
                  onClick={() => handleExport('png')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--tx2)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)]"
                >
                  <ImageDown className="w-3.5 h-3.5 text-[var(--blue-d)]" />
                  Exporter en PNG
                </button>
                <button
                  onClick={() => handleExport('svg')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--tx2)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)]"
                >
                  <FileDown className="w-3.5 h-3.5 text-[var(--green-text)]" />
                  Exporter en SVG
                </button>
                <hr className="my-1 border-[var(--bd)]" />
                <button
                  onClick={() => {
                    window.print()
                    setShowExport(false)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--tx2)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)]"
                >
                  <FileDown className="w-3.5 h-3.5 text-[var(--tx3)]" />
                  Imprimer / PDF
                </button>
              </div>
            )}
          </div>

          {canManage && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-xs font-medium transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Ajouter poste
            </button>
          )}
        </div>
      </div>

      {/* ── React Flow ───────────────────────────────────────────────────── */}
      <div className="flex-1 relative" ref={flowWrapperRef}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={() => setSelectedPos(null)}
          nodeTypes={NODE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          className="bg-[var(--surface-2)]"
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={canManage}
        >
          <Background color="#1e293b" gap={24} size={1} />
          <Controls
            className="!bg-white !border-[var(--bd)]
              [&>button]:!bg-white [&>button]:!border-[var(--bd2)]
              [&>button]:!text-[var(--tx2)] [&>button:hover]:!bg-[var(--surface-2)]"
          />
          <MiniMap
            className="!bg-white !border-[var(--bd)]"
            nodeColor={(n) => ((n.data as any)?.position?.isVacant ? '#475569' : '#3b82f6')}
          />
        </ReactFlow>

        {/* Empty state */}
        {filteredPositions.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--tx3)] pointer-events-none">
            <Users className="w-12 h-12 mb-3 opacity-20" />
            <p className="text-sm font-medium">Aucune position configurée</p>
            {canManage && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm pointer-events-auto"
              >
                <Plus className="w-4 h-4" /> Créer la première position
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Panel latéral détail ─────────────────────────────────────────── */}
      {selectedPos && (
        <PositionDetailPanel
          position={selectedPos}
          canManage={canManage}
          onClose={() => setSelectedPos(null)}
          onUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
            queryClient.invalidateQueries({ queryKey: ['orgchart-stats'] })
            setSelectedPos(null)
          }}
        />
      )}

      {/* ── Modal ajout poste ────────────────────────────────────────────── */}
      {showAddModal && (
        <AddPositionModal
          positions={positions as OrgPosition[]}
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
            queryClient.invalidateQueries({ queryKey: ['orgchart-stats'] })
            setShowAddModal(false)
          }}
        />
      )}

      {/* ── Dialog confirmation drag & drop ─────────────────────────────── */}
      {pendingMove && movedPosition && newParentPosition && (
        <MoveConfirmDialog
          movedPosition={movedPosition}
          newParentPosition={newParentPosition}
          oldParentPosition={oldParentPosition}
          subordinateCount={subordinateCount}
          isLoading={moveMutation.isPending}
          onConfirm={confirmMove}
          onCancel={cancelMove}
        />
      )}
    </div>
  )
}

// ============================================================================
//  DIALOG CONFIRMATION DÉPLACEMENT (drag & drop)
// ============================================================================

function MoveConfirmDialog({
  movedPosition,
  newParentPosition,
  oldParentPosition,
  subordinateCount,
  isLoading,
  onConfirm,
  onCancel,
}: {
  movedPosition: OrgPosition
  newParentPosition: OrgPosition
  oldParentPosition: OrgPosition | null | undefined
  subordinateCount: number
  isLoading: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const movedUser = movedPosition.employee?.user
  const newParentUser = newParentPosition.employee?.user
  const oldParentUser = oldParentPosition?.employee?.user

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.45)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-[var(--bd)]">
          <div className="p-2 bg-[var(--amber-bg)] rounded-xl">
            <AlertTriangle className="w-5 h-5 text-[var(--amber-text)]" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--tx)]">Confirmer le déplacement</h2>
            <p className="text-xs text-[var(--tx3)] mt-0.5">Cette action modifie la hiérarchie</p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Visualisation du mouvement */}
          <div className="flex items-center gap-3 p-4 bg-[var(--surface-2)] rounded-xl">
            {/* Poste déplacé */}
            <div className="flex-1 text-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center mx-auto mb-1">
                {movedUser ? (
                  <span className="text-[var(--tx)] text-xs font-bold">
                    {movedUser.firstName?.[0]}
                    {movedUser.lastName?.[0]}
                  </span>
                ) : (
                  <User className="w-4 h-4 text-[var(--tx)]" />
                )}
              </div>
              <p className="text-xs font-semibold text-[var(--tx)] line-clamp-1">{movedPosition.title}</p>
              {movedUser && (
                <p className="text-[10px] text-[var(--tx3)]">
                  {movedUser.firstName} {movedUser.lastName}
                </p>
              )}
            </div>

            <MoveRight className="w-5 h-5 text-[var(--amber-text)] shrink-0" />

            {/* Nouveau parent */}
            <div className="flex-1 text-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-1">
                {newParentUser ? (
                  <span className="text-[var(--tx)] text-xs font-bold">
                    {newParentUser.firstName?.[0]}
                    {newParentUser.lastName?.[0]}
                  </span>
                ) : (
                  <User className="w-4 h-4 text-[var(--tx)]" />
                )}
              </div>
              <p className="text-xs font-semibold text-[var(--tx)] line-clamp-1">
                {newParentPosition.title}
              </p>
              {newParentUser && (
                <p className="text-[10px] text-[var(--tx3)]">
                  {newParentUser.firstName} {newParentUser.lastName}
                </p>
              )}
            </div>
          </div>

          {/* Résumé de l'impact */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider">
              Impact RH
            </p>

            <div className="space-y-1.5 text-xs">
              {oldParentPosition && (
                <div className="flex items-start gap-2">
                  <span className="text-[var(--tx3)] shrink-0">Ancien responsable :</span>
                  <span className="text-[var(--tx2)]">
                    {oldParentUser
                      ? `${oldParentUser.firstName} ${oldParentUser.lastName} (${oldParentPosition.title})`
                      : oldParentPosition.title}
                  </span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <span className="text-[var(--tx3)] shrink-0">Nouveau responsable :</span>
                <span className="text-emerald-300 font-medium">
                  {newParentUser
                    ? `${newParentUser.firstName} ${newParentUser.lastName} (${newParentPosition.title})`
                    : newParentPosition.title}
                </span>
              </div>
              {subordinateCount > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-[var(--tx3)] shrink-0">Subordonnés concernés :</span>
                  <span className="text-amber-300 font-medium">
                    {subordinateCount} poste{subordinateCount > 1 ? 's' : ''} également déplacé
                    {subordinateCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>

          {subordinateCount > 0 && (
            <div className="flex items-start gap-2 p-3 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg">
              <AlertTriangle className="w-3.5 h-3.5 text-[var(--amber-text)] shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">
                Toute la sous-arborescence de <strong>{movedPosition.title}</strong> sera déplacée
                avec ce poste ({subordinateCount} subordonné{subordinateCount > 1 ? 's' : ''}).
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-5 pb-5">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)] transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-500 text-[var(--tx)] rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {isLoading ? <Spinner /> : <Check className="w-4 h-4" />}
            Confirmer le déplacement
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Panel détail position ────────────────────────────────────────────────────

function PositionDetailPanel({
  position,
  canManage,
  onClose,
  onUpdated,
}: {
  position: OrgPosition
  canManage: boolean
  onClose: () => void
  onUpdated: () => void
}) {
  const addNotif = useNotificationStore((s) => s.addNotification)
  const queryClient = useQueryClient()
  const user = position.employee?.user

  const [showAssign, setShowAssign] = useState(false)
  const [assignSearch, setAssignSearch] = useState('')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editTitle, setEditTitle] = useState(position.title)

  // Mutation renommage
  const renameMutation = useMutation({
    mutationFn: () => orgchartApi.updatePosition(position.id, { title: editTitle.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
      setIsEditingTitle(false)
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Poste renommé' })
    },
    onError: () => addNotif({ id: Date.now().toString(), type: 'error', title: 'Erreur lors du renommage' }),
  })

  // Recherche employés pour assignation
  const { data: employeesData } = useQuery({
    queryKey: ['employees-assign', assignSearch],
    queryFn: () => api.get('/hr/employees', { params: { search: assignSearch, limit: 10 } }).then(r => r.data.data),
    enabled: showAssign && assignSearch.length >= 2,
  })
  // HR employees retourne { employees: [], total, page... }
  const availableEmployees: any[] = employeesData?.employees ?? []

  const assignMutation = useMutation({
    mutationFn: (employeeId: string) => {
      // Récupérer le département de l'employé sélectionné
      const emp = availableEmployees.find((e: any) => e.id === employeeId)
      const departmentId = emp?.departmentId ?? emp?.department?.id ?? undefined
      return orgchartApi.updatePosition(position.id, {
        employeeId,
        ...(departmentId ? { departmentId } : {}),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
      queryClient.invalidateQueries({ queryKey: ['orgchart-stats'] })
      setShowAssign(false)
      setAssignSearch('')
      onClose()
    },
  })

  const unassignMutation = useMutation({
    mutationFn: () =>
      // FIX: passer isVacant: true explicitement pour que le poste passe en vacant
      orgchartApi.updatePosition(position.id, { employeeId: null, isVacant: true } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgchart-flat'] })
      queryClient.invalidateQueries({ queryKey: ['orgchart-stats'] })
      onClose()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => orgchartApi.deletePosition(position.id),
    onSuccess: () => {
      onUpdated()
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Position supprimée' })
    },
    onError: (err: any) => {
      addNotif({
        id: Date.now().toString(),
        type: 'error',
        title: err?.response?.data?.message ?? 'Impossible de supprimer cette position',
      })
    },
  })

  return (
    <div className="absolute top-4 right-4 w-72 bg-white border border-[var(--bd)] rounded-2xl shadow-2xl z-20 animate-in slide-in-from-right-4">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bd)]">
        <h3 className="text-sm font-semibold text-[var(--tx)]">Détail du poste</h3>
        <button onClick={onClose} className="p-1 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
          <X className="w-4 h-4 text-[var(--tx3)]" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          {position.isVacant ? (
            <div className="w-12 h-12 rounded-full border-2 border-dashed border-[var(--bd2)] flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-[var(--tx4)]" />
            </div>
          ) : user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="w-12 h-12 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center shrink-0">
              <span className="text-[var(--tx)] font-bold text-sm">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </span>
            </div>
          )}
          <div>
            {position.isVacant ? (
              <p className="text-sm text-[var(--tx3)] italic">Poste vacant</p>
            ) : (
              <p className="text-sm font-semibold text-[var(--tx)]">
                {user?.firstName} {user?.lastName}
              </p>
            )}
            {isEditingTitle ? (
              <div className="flex items-center gap-1 mt-0.5">
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editTitle.trim()) renameMutation.mutate()
                    if (e.key === 'Escape') setIsEditingTitle(false)
                  }}
                  autoFocus
                  className="flex-1 text-xs px-2 py-1 bg-white border border-[var(--blue)]/40 rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
                />
                <button
                  onClick={() => editTitle.trim() && renameMutation.mutate()}
                  disabled={renameMutation.isPending}
                  className="p-1 bg-[var(--blue)] text-white rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                </button>
                <button onClick={() => setIsEditingTitle(false)} className="p-1 hover:bg-[var(--surface-2)] rounded-md">
                  <X className="w-3 h-3 text-[var(--tx3)]" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setEditTitle(position.title); setIsEditingTitle(true) }}
                className="group/title flex items-center gap-1 text-xs text-[var(--tx3)] hover:text-[var(--tx)] transition-colors text-left"
              >
                {position.title}
                <Edit2 className="w-3 h-3 opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0" />
              </button>
            )}
            {position.department && (
              <p className="text-[10px] text-[var(--tx3)]">{position.department.name}</p>
            )}
          </div>
        </div>

        {!position.isVacant && user?.email && (
          <div className="flex items-center gap-2 text-xs text-[var(--tx3)]">
            <Mail className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{user.email}</span>
          </div>
        )}

        <div className="flex items-center justify-between text-xs">
          <span className="text-[var(--tx3)]">Niveau hiérarchique</span>
          <span className="text-[var(--tx)] font-medium">{position.level}</span>
        </div>

        {position.isVacant && (
          <div className="p-2.5 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg">
            <p className="text-xs text-amber-300">Poste ouvert — en attente de recrutement</p>
          </div>
        )}

        {canManage && (
          <div className="pt-2 border-t border-[var(--bd)] space-y-2">
            {/* Bouton assigner / désassigner */}
            {position.isVacant ? (
              <button
                onClick={() => setShowAssign(true)}
                className="w-full py-2 text-xs font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg transition-colors flex items-center justify-center gap-1.5"
              >
                <UserPlus className="w-3.5 h-3.5" /> Assigner un employé
              </button>
            ) : (
              <button
                onClick={() => unassignMutation.mutate()}
                disabled={unassignMutation.isPending}
                className="w-full py-2 text-xs font-medium border border-[var(--bd)] hover:bg-[var(--red-bg)] text-[var(--red-text)] rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {unassignMutation.isPending ? <Spinner /> : <UserMinus className="w-3.5 h-3.5" />}
                Désassigner l'employé
              </button>
            )}

            {/* Modal assignation */}
            {showAssign && (
              <div className="border border-[var(--bd)] rounded-xl p-3 bg-[var(--surface-2)] space-y-2">
                <p className="text-xs font-medium text-[var(--tx)]">Rechercher un employé</p>
                <input
                  value={assignSearch}
                  onChange={e => setAssignSearch(e.target.value)}
                  placeholder="Nom, prénom… (min 2 caractères)"
                  autoFocus
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
                />
                {assignSearch.length >= 2 && (
                  <div className="bg-white border border-[var(--bd)] rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                    {availableEmployees.length === 0 ? (
                      <p className="text-xs text-[var(--tx3)] text-center py-3">Aucun employé trouvé</p>
                    ) : (
                      availableEmployees.map((emp: any) => (
                        <button
                          key={emp.id}
                          onClick={() => assignMutation.mutate(emp.id)}
                          disabled={assignMutation.isPending}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--surface-2)] text-left transition-colors disabled:opacity-50"
                        >
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-white text-[9px] font-bold shrink-0">
                            {emp.firstName?.[0]}{emp.lastName?.[0]}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-[var(--tx)] truncate">{emp.firstName} {emp.lastName}</p>
                            <p className="text-[10px] text-[var(--tx3)] truncate">{emp.jobTitle ?? '—'}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
                <button
                  onClick={() => { setShowAssign(false); setAssignSearch('') }}
                  className="w-full py-1 text-xs text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
                >
                  Annuler
                </button>
              </div>
            )}

            <button
              onClick={() => {
                if (window.confirm('Supprimer ce poste ? Cette action est irréversible.')) {
                  deleteMutation.mutate()
                }
              }}
              disabled={deleteMutation.isPending}
              className="w-full py-1.5 text-xs text-[var(--red-text)] hover:bg-[#F87171]/10 rounded-lg transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {deleteMutation.isPending ? <Spinner /> : null}
              Supprimer ce poste
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Modal ajout position ─────────────────────────────────────────────────────

function AddPositionModal({
  positions,
  onClose,
  onCreated,
}: {
  positions: OrgPosition[]
  onClose: () => void
  onCreated: () => void
}) {
  const addNotif = useNotificationStore((s) => s.addNotification)
  const [form, setForm] = useState({ title: '', parentId: '', departmentId: '' })
  const f = (k: string) => (e: any) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      orgchartApi.createPosition({
        title: form.title,
        parentId: form.parentId || undefined,
        departmentId: form.departmentId || undefined,
      }),
    onSuccess: () => {
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Poste créé' })
      onCreated()
    },
    onError: () => {
      addNotif({ id: Date.now().toString(), type: 'error', title: 'Erreur lors de la création' })
    },
  })

  const inputCls =
    'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]'
  const occupiedPositions = positions.filter((p) => !p.isVacant)

  const { data: depts = [] } = useQuery({
    queryKey: ['departments-orgchart'],
    queryFn: () => api.get('/hr/departments').then(r => r.data.data ?? []),
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Ajouter un poste</h2>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Intitulé du poste *
            </label>
            <input
              className={inputCls}
              value={form.title}
              onChange={f('title')}
              placeholder="Ex: Directeur Commercial"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Responsable hiérarchique
            </label>
            <select className={inputCls} value={form.parentId} onChange={f('parentId')}>
              <option value="">Racine (aucun parent)</option>
              {occupiedPositions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.employee?.user?.firstName} {p.employee?.user?.lastName} — {p.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Département
            </label>
            <select className={inputCls} value={form.departmentId} onChange={f('departmentId')}>
              <option value="">Aucun département</option>
              {(depts as any[]).map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            {(depts as any[]).length === 0 && (
              <p className="text-[10px] text-[var(--tx3)] mt-1">Aucun département créé — gérez-les depuis RH.</p>
            )}
          </div>
          <p className="text-[10px] text-[var(--tx3)]">
            Le poste sera créé comme vacant. Vous pourrez ensuite assigner un employé depuis ce panel.
          </p>
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => mutate()}
              disabled={isPending || !form.title.trim()}
              className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
